import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState, type FormEvent } from "react";
import { Mail, MapPin, Clock, Calendar, ArrowRight } from "lucide-react";
import { CALENDLY_URL } from "@/lib/site";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/Sections";
import { industries, pricingPlans, services } from "@/lib/finendra-data";
import { useServerFn } from "@tanstack/react-start";
import { leadSchema } from "@/lib/contact-schema";
import { submitLead } from "@/lib/contact.functions";
import { trackFormSubmission, trackScheduleConsultation } from "@/lib/analytics";

type ContactSearch = {
  service?: string | undefined;
  plan?: string | undefined;
  industry?: string | undefined;
};

export const Route = createFileRoute("/contact")({
  validateSearch: (search: Record<string, unknown>): ContactSearch => ({
    ...(typeof search["service"] === "string" ? { service: search["service"] } : {}),
    ...(typeof search["plan"] === "string" ? { plan: search["plan"] } : {}),
    ...(typeof search["industry"] === "string" ? { industry: search["industry"] } : {}),
  }),

  head: () => ({
    meta: [
      { title: "Contact Finendra | Schedule a Consultation" },
      {
        name: "description",
        content:
          "Talk to Finendra about outsourced bookkeeping, payroll, AP/AR, and controller support. Tell us about your business and we'll respond within one business day.",
      },
      { property: "og:title", content: "Contact Finendra" },
      {
        property: "og:description",
        content: "Schedule a consultation with a global finance and accounting outsourcing team.",
      },
      { property: "og:url", content: "https://finendra.com/contact" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Finendra" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:title", content: "Contact Finendra" },
      {
        name: "twitter:description",
        content: "Schedule a consultation with a global finance and accounting outsourcing team.",
      },
      { name: "twitter:image:alt", content: "Contact Finendra" },
      { property: "og:image", content: "https://finendra.com/og/og-contact.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://finendra.com/og/og-contact.png" },
    ],
    links: [{ rel: "canonical", href: "https://finendra.com/contact" }],
  }),
  component: ContactPage,
});

const inputClass =
  "w-full rounded-xl border border-brand-navy/10 bg-background px-4 py-3 text-sm text-brand-navy outline-none transition-colors focus:border-brand-accent";

function ContactPage() {
  const search = Route.useSearch();
  const renderedAt = useRef(Date.now());
  const send = useServerFn(submitLead);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const planOptions = pricingPlans.map((p) => `Plan: ${p.name}`);
  const serviceTitles = [...planOptions, ...services.map((s) => s.title)];
  const rawPrefill = search.service ?? (search.plan ? `Plan: ${search.plan}` : "");
  const matchedService = serviceTitles.find(
    (st) =>
      st.toLowerCase() === rawPrefill.toLowerCase() ||
      (search.plan && st.toLowerCase() === `plan: ${search.plan.toLowerCase()}`),
  );
  const prefillService = matchedService ?? "";
  const matchedIndustry = industries.find(
    (i) =>
      i.name.toLowerCase() === search.industry?.toLowerCase() ||
      (search.industry && search.industry.toLowerCase().includes(i.name.toLowerCase())),
  );
  const prefillIndustry = matchedIndustry ? matchedIndustry.name : (search.industry ?? "");

  const prefillMessage = prefillService
    ? `We're interested in ${prefillService}${
        prefillIndustry ? ` for our ${prefillIndustry} business` : ""
      }. Here's where things stand today: `
    : prefillIndustry
      ? `We're looking for finance operations support for our ${prefillIndustry} business. Here's our current volume: `
      : "";

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const form = new FormData(e.currentTarget);
    const industry = String(form.get("industry") ?? "");
    const message = String(form.get("message") ?? "");
    const payload = {
      name: String(form.get("name") ?? ""),
      email: String(form.get("email") ?? ""),
      company: String(form.get("company") ?? ""),
      phone: String(form.get("phone") ?? ""),
      service: String(form.get("service") ?? ""),
      message: industry ? `${message}\n\nIndustry: ${industry}` : message,
      source: prefillService ? "service_page_cta" : "contact_page",
      website: String(form.get("website") ?? ""),
      renderedAt: renderedAt.current,
    };

    // Client-side validation mirrors the server schema for instant feedback.
    const parsed = leadSchema.safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Please check your details.");
      return;
    }

    setSubmitting(true);
    const result = await send({ data: payload });
    setSubmitting(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    renderedAt.current = Date.now();
    setSent(true);
    trackFormSubmission("contact_page", {
      service: payload.service,
      industry: industry || undefined,
      source: payload.source,
    });
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's talk about your books."
        body="Tell us where your finance operation stands today. We'll come back with a clear scope, a fixed monthly fee, and a start date."
      />

      <section className="px-6 pb-28">
        <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-[1.4fr_1fr]">
          <Reveal>
            <div className="rounded-3xl border border-brand-navy/5 bg-brand-surface p-8 md:p-10">
              {sent ? (
                <div className="py-16 text-center">
                  <h2 className="font-display text-3xl font-bold text-brand-navy">
                    Thank you — message received.
                  </h2>
                  <p className="mx-auto mt-4 max-w-md text-brand-navy/60">
                    A Finendra specialist will follow up within one business day. For anything
                    urgent, email connect@finendra.com.
                  </p>
                  <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                    <a
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackScheduleConsultation("contact_page_thank_you")}
                      data-analytics-location="contact_page_thank_you"
                      className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-6 py-3 text-sm font-semibold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-brand-accent hover:shadow-lg active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
                    >
                      Schedule a Consultation on Calendly <ArrowRight className="size-4" />
                    </a>
                    <button
                      type="button"
                      onClick={() => setSent(false)}
                      className="rounded-xl border border-brand-navy/10 px-6 py-3 text-sm font-semibold text-brand-navy transition-colors hover:bg-background"
                    >
                      Send another message
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  key={`${prefillService}|${prefillIndustry}`}
                  onSubmit={onSubmit}
                  className="space-y-5"
                >
                  {prefillService && (
                    <p className="rounded-xl border border-brand-accent/30 bg-brand-accent/5 px-4 py-3 text-sm text-brand-navy/70">
                      Pre-filled for{" "}
                      <strong className="font-semibold text-brand-navy">{prefillService}</strong>
                      {prefillIndustry ? (
                        <>
                          {" "}
                          in{" "}
                          <strong className="font-semibold text-brand-navy">
                            {prefillIndustry}
                          </strong>
                        </>
                      ) : null}
                      . Change anything below before sending.
                    </p>
                  )}

                  <div className="grid gap-5 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="name"
                        className="mb-2 block text-sm font-medium text-brand-navy"
                      >
                        Full name
                      </label>
                      <input id="name" name="name" required className={inputClass} />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-medium text-brand-navy"
                      >
                        Work email
                      </label>
                      <input id="email" name="email" type="email" required className={inputClass} />
                    </div>
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="company"
                        className="mb-2 block text-sm font-medium text-brand-navy"
                      >
                        Company
                      </label>
                      <input id="company" name="company" className={inputClass} />
                    </div>
                    <div>
                      <label
                        htmlFor="service"
                        className="mb-2 block text-sm font-medium text-brand-navy"
                      >
                        Service of interest
                      </label>
                      <select
                        id="service"
                        name="service"
                        defaultValue={prefillService || serviceTitles[0]}
                        className={inputClass}
                      >
                        {serviceTitles.map((st) => (
                          <option key={st}>{st}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="industry"
                      className="mb-2 block text-sm font-medium text-brand-navy"
                    >
                      Industry <span className="text-brand-navy/40">(optional)</span>
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      defaultValue={prefillIndustry}
                      className={inputClass}
                    >
                      <option value="">Select your industry</option>
                      {industries.map((i) => (
                        <option key={i.name}>{i.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="mb-2 block text-sm font-medium text-brand-navy"
                    >
                      Phone <span className="text-brand-navy/40">(optional)</span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      className={inputClass}
                    />
                  </div>

                  {/* Honeypot — hidden from people, tempting to bots. */}
                  <div aria-hidden="true" className="hidden">
                    <label htmlFor="website">Website</label>
                    <input
                      id="website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="mb-2 block text-sm font-medium text-brand-navy"
                    >
                      How can we help?
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      defaultValue={prefillMessage}
                      className={inputClass}
                    />
                  </div>

                  {error && (
                    <p role="alert" className="text-sm font-medium text-destructive">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-xl bg-brand-navy px-8 py-4 font-semibold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-brand-accent active:scale-[0.985] active:duration-75 motion-reduce:transform-none select-none disabled:cursor-not-allowed disabled:opacity-60 disabled:transform-none"
                  >
                    {submitting ? "Sending…" : "Request a Consultation"}
                  </button>

                  <div className="flex items-center gap-2 text-xs text-brand-navy/60">
                    <span className="font-semibold text-brand-navy">
                      🔒 Confidential &amp; Protected:
                    </span>
                    <span>
                      We never share or sell your details. We respond with a scoped proposal within
                      one business day.
                    </span>
                  </div>
                </form>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="space-y-8">
            <div className="flex gap-4">
              <Mail className="mt-1 size-5 shrink-0 text-brand-accent" />
              <div>
                <h2 className="font-bold text-brand-navy">Email</h2>
                <a
                  href="mailto:connect@finendra.com"
                  className="text-sm text-brand-navy/60 hover:text-brand-accent"
                >
                  connect@finendra.com
                </a>
              </div>
            </div>
            <div className="flex gap-4">
              <Clock className="mt-1 size-5 shrink-0 text-brand-accent" />
              <div>
                <h2 className="font-bold text-brand-navy">Hours</h2>
                <p className="text-sm text-brand-navy/60">
                  Monday to Friday, 9:00am – 6:00pm ET. Responses within one business day.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <MapPin className="mt-1 size-5 shrink-0 text-brand-accent" />
              <div>
                <h2 className="font-bold text-brand-navy">Coverage</h2>
                <p className="text-sm text-brand-navy/60">
                  Serving businesses worldwide across all major time zones, fully remote and
                  cloud-based.
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-brand-accent/30 bg-gradient-to-br from-brand-accent/10 to-brand-surface p-6 shadow-sm">
              <div className="flex items-center gap-2 font-bold text-brand-navy">
                <Calendar className="size-5 text-brand-accent shrink-0" />
                <span>Instant Scheduling</span>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-brand-navy/70">
                Want to skip the form and pick a time directly? Book a 20-minute scoping call on our
                calendar.
              </p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackScheduleConsultation("contact_page_instant_scheduling")}
                data-analytics-location="contact_page_instant_scheduling"
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-brand-navy px-5 py-2.5 text-xs font-bold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-brand-accent hover:shadow-md active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
              >
                Schedule a Consultation <ArrowRight className="size-3.5" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
